import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App as AntdApp, Button, Popconfirm } from 'antd';

import ContextBar from '../components/common/ContextBar';
import TransferOfferSummary, { offeredBy } from '../components/transfers/TransferOfferSummary';
import { DataState, ResourceState, Skeleton, deriveDataState, resourceStateOf } from '../components/loading';
import {
	acceptTransferOffer,
	cancelTransferOffer,
	declineTransferOffer,
	getTransferOffer,
	transferOfferRefusal,
} from '../services/transferOffers';
import { formatDisplayDate } from '../utils/dateDisplay';
import { normalizeId } from '../utils/ids';

/** What happened to an offer that is no longer waiting, in one sentence. */
/** An offer that is gone, or was never the caller's: one sentence for both. */
export const OFFER_NOT_FOUND_TEXT = 'This offer is not here. It may have been withdrawn, or made to someone else.';

export function offerOutcome(offer) {
	const name = offer?.collection?.name || 'this collection';
	const on = offer?.decidedAt ? ` on ${formatDisplayDate(offer.decidedAt)}` : '';
	const mine = offer?.isRecipient === true;
	switch (offer?.status) {
	case 'accepted': return mine
		? `You accepted ${name}${on}. It is yours now.`
		: `${offer?.recipient?.name || 'The new owner'} accepted ${name}${on}. It is theirs now.`;
	case 'declined': return mine ? `You declined ${name}${on}. Nothing changed.` : `${name} was declined${on}. Nothing changed.`;
	case 'cancelled': return `This offer of ${name} was withdrawn${on}. Nothing changed.`;
	case 'expired': return `This offer of ${name} ended${on} without an answer. Nothing changed.`;
	case 'closed': return `This offer of ${name} could not go ahead: ${offer?.closedMessage || 'something changed after it was made.'} Nothing changed.`;
	default: return null;
	}
}

/**
 * A transfer offer (KPR-743, keepr-api docs/SCHEMA.md "Transfer offers"):
 * where the notice's and the email's Accept and Decline buttons land, and
 * where Home's rows lead. The person it is offered to sees what it is — the
 * collection's badge, what it holds, who offers it, when the offer ends — and
 * accepts or declines it; the person who made it sees it waiting and may
 * cancel it. `?answer=accept|decline` only puts the focus on that button:
 * opening a link never answers for anyone.
 */
export default function TransferOfferPage() {
	const { offerId: rawId } = useParams();
	const offerId = normalizeId(rawId) || rawId;
	const [searchParams] = useSearchParams();
	const answer = searchParams.get('answer');
	const { message } = AntdApp.useApp();
	const navigate = useNavigate();
	const [offer, setOffer] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [notFound, setNotFound] = useState(false);
	const [busy, setBusy] = useState(null); // 'accept' | 'decline' | 'cancel'
	const [refusal, setRefusal] = useState(null);
	const [accepted, setAccepted] = useState(null); // the accept's answer
	const requestRef = useRef(0);

	const load = useCallback(() => {
		const request = ++requestRef.current;
		setLoading(true);
		setLoadError(null);
		setNotFound(false);
		getTransferOffer(offerId)
			.then(row => { if (request === requestRef.current) { setOffer(row); setLoading(false); } })
			.catch(error => {
				if (request !== requestRef.current) return;
				if (resourceStateOf(error, { noAccessCodes: [] }) === 'not-found') setNotFound(true);
				else setLoadError(error);
				setLoading(false);
			});
	}, [offerId]);

	useEffect(() => {
		load();
		return () => { requestRef.current += 1; };
	}, [load]);

	const act = async (verb) => {
		setBusy(verb);
		setRefusal(null);
		try {
			if (verb === 'accept') {
				const result = await acceptTransferOffer(offerId);
				setAccepted(result);
				setOffer(result?.offer || offer);
				message.success(`You own ${offer?.collection?.name || 'the collection'} now.`);
			} else if (verb === 'decline') {
				setOffer(await declineTransferOffer(offerId));
				message.success('Declined. Nothing changed.');
			} else {
				setOffer(await cancelTransferOffer(offerId));
				message.success('Offer cancelled. Nothing changed.');
			}
		} catch (error) {
			setRefusal(transferOfferRefusal(error));
			// The offer may have closed or moved on: show it as it is now.
			getTransferOffer(offerId).then(setOffer).catch(() => {});
		} finally {
			setBusy(null);
		}
	};

	const collectionId = normalizeId(accepted?.collection?._id || offer?.collection_id);
	const waiting = offer?.status === 'pending';

	return (
		<div className="transfer-offer-page">
			<ContextBar title="Transfer offer" />
			{/* Gone, or never the caller's (the API answers one 404 for both): a
			    resource state, never an empty list. */}
			{notFound ? (
				<ResourceState state="not-found" body={OFFER_NOT_FOUND_TEXT} />
			) : (
			<DataState
				state={deriveDataState({ loading, error: loadError, hasData: offer !== null })}
				hasData={offer !== null}
				skeleton={<Skeleton variant="block" count={1} />}
				error={loadError ? 'Check your connection and try again.' : null}
				onRetry={load}
				label="the offer"
			>
				{offer ? (
					<section className="form-panel transfer-offer-page__panel" aria-label="The offer">
						{offer.canAnswer || (waiting && !offer.canCancel) ? (
							<p className="transfer-offer-page__lead">
								{offer.kind === 'handover'
									? `${offeredBy(offer)} offers you this collection, published by ${offer.publisher?.name || 'your publisher'}.`
									: `${offeredBy(offer)} offers you this collection.`}
							</p>
						) : waiting ? (
							<p className="transfer-offer-page__lead">
								{`You offered this collection to ${offer.recipient?.name || offer.recipient?.email || 'someone'}${offer.recipient?.email && offer.recipient?.name ? ` (${offer.recipient.email})` : ''}. It is still yours until they accept.`}
							</p>
						) : null}

						<TransferOfferSummary offer={offer} size="lg" showSubCollections />

						{offer.note ? <p className="transfer-offer-page__note">{`Their note: ${offer.note}`}</p> : null}

						{offer.canAnswer ? (
							<p className="transfer-offer-page__explain">
								Accepting makes you its owner: you become responsible for its items, the people it is shared
								with and the space its files take. Nothing changes until you accept.
							</p>
						) : null}

						{!waiting ? <Alert type="info" showIcon message={offerOutcome(offer)} /> : null}
						{refusal ? <Alert type="warning" showIcon message={refusal} /> : null}

						{offer.canAnswer ? (
							<div className="transfer-offer-page__actions">
								<Button
									onClick={() => act('decline')}
									loading={busy === 'decline'}
									disabled={Boolean(busy)}
									autoFocus={answer === 'decline'}
								>
									Decline
								</Button>
								<Button
									type="primary"
									onClick={() => act('accept')}
									loading={busy === 'accept'}
									disabled={Boolean(busy)}
									autoFocus={answer !== 'decline'}
								>
									Accept
								</Button>
							</div>
						) : null}

						{offer.canCancel ? (
							<div className="transfer-offer-page__actions">
								<Popconfirm
									title="Cancel this offer?"
									description="They will no longer be able to accept it."
									okText="Cancel offer"
									cancelText="Keep it"
									onConfirm={() => act('cancel')}
								>
									<Button loading={busy === 'cancel'} disabled={Boolean(busy)}>Cancel offer</Button>
								</Popconfirm>
							</div>
						) : null}

						{offer.status === 'accepted' && accepted && collectionId ? (
							<div className="transfer-offer-page__actions">
								<Button type="primary" onClick={() => navigate(`/collections/${collectionId}`)}>
									Open the collection
								</Button>
							</div>
						) : null}
					</section>
				) : null}
			</DataState>
			)}
		</div>
	);
}
